import {useCallback, useRef, useState} from 'react';
import axiosApiInstance from '../../../api/axiosClient';

export function useLazyReleaseTracks() {
    const [tracksByReleaseId, setTracksByReleaseId] = useState<
        Record<string, API.MusicDistribution.ArtTrack[]>
    >({});
    const [loadingId, setLoadingId] = useState<string | null>(null);
    const loadedOrLoadingRef = useRef<Set<string>>(new Set());

    const loadTracksForRelease = useCallback(
        async (release: API.MusicDistribution.MusicRelease) => {
            if (!release.id) {
                return;
            }
            if (loadedOrLoadingRef.current.has(release.id)) {
                return;
            }
            // List responses include ISRC-only track stubs. Those are enough for
            // the ISRC column; the expanded row still needs titles and artists.
            const embedded = release.art_tracks;
            if (embedded?.some((track) => Boolean(track.title))) {
                loadedOrLoadingRef.current.add(release.id);
                setTracksByReleaseId((prev) => ({
                    ...prev,
                    [release.id]: embedded,
                }));
                return;
            }
            loadedOrLoadingRef.current.add(release.id);
            setLoadingId(release.id);
            try {
                const {data} = await axiosApiInstance.get<API.MusicDistribution.ArtTrack[]>(
                    '/api/products/art_tracks/',
                    {params: {release_id: release.id}},
                );
                setTracksByReleaseId((prev) => ({...prev, [release.id]: data || []}));
            } catch (e) {
                console.error(e);
                loadedOrLoadingRef.current.delete(release.id);
                setTracksByReleaseId((prev) => ({...prev, [release.id]: []}));
            } finally {
                setLoadingId(null);
            }
        },
        [],
    );

    const tracksForRelease = (release: API.MusicDistribution.MusicRelease) =>
        (release.id && tracksByReleaseId[release.id]) || release.art_tracks || [];

    return {loadTracksForRelease, tracksForRelease, tracksLoadingId: loadingId};
}

export function releaseIsArchived(record: API.MusicDistribution.MusicRelease): boolean {
    return (record.archive_status?.length ?? 0) > 0;
}
